// A channel's overflow menu and its dialogs (M2-06, CHAT-UX; CHAT-CONTROLS): the bell (the
// person's notification level and mute), and the "…" with Channel details (About, Members,
// Settings and permissions, Danger zone: channel-details.tsx), the digital employees' settings,
// Saved messages and Leave. A topic's own controls are topic-actions.tsx. The agents' rules are
// visible to every member (agents-in-chat research recs. 3 and 17); CHAT-DE (2026-10-27) made
// them one compact "Digital employees" row in Channel details › Settings (`AgentRulesRow`): the
// wake rule as a select, the standing instructions behind a small button.
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BellIcon,
  BellOffIcon,
  BookmarkIcon,
  BotIcon,
  EllipsisIcon,
  InfoIcon,
  LogOutIcon,
  NotebookPenIcon,
  Settings2Icon,
  UsersIcon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { keepFocus } from "../../focus/focus";
import { isManager } from "../company";
import { problemText } from "../ops-parts";
import { ChannelDetailsDialog, type DetailsTab } from "./channel-details";
import { isDm } from "./chat-model";
import {
  type AgentRules,
  type Channel,
  type ChannelPrefs,
  chatKeys,
  membersQuery,
  prefsQuery,
  rulesQuery,
} from "./chat-queries";
import type { ChatContext } from "./use-chat";

/**
 * Whether the person manages the channel (the API's `access.manage`): a company owner or
 * admin, or an admin row in the channel. Unpin, archive, restore and delete show for them only.
 */
export function useCanManage(chat: ChatContext, channel: Pick<Channel, "id">): boolean {
  const members = useQuery({
    ...membersQuery(chat.api, chat.co, channel.id),
    enabled: chat.meId !== null && !isManager(chat.company),
    retry: false,
  });
  if (chat.meId === null || chat.readOnly) return false;
  if (isManager(chat.company)) return true;
  return (
    members.data?.data.some((m) => m.membership_id === chat.meId && m.role === "admin") ?? false
  );
}

type Notify = ChannelPrefs["notify"];
type Wake = AgentRules["wake"];
type Initiate = AgentRules["initiate"];

/** DE-INITIATE: what DEs may start here unprompted (it only narrows the company's rules). */
export const INITIATE_LABEL: Record<Initiate, string> = {
  allowed: "May start threads",
  routines: "Routines only",
  off: "Start nothing",
};

const NOTIFY_LABEL: Record<Notify, string> = {
  all: "All messages",
  mentions: "Mentions only",
  none: "Nothing",
};

export const WAKE_LABEL: Record<Wake, string> = {
  mentions: "When @mentioned",
  topic: "Every message",
  silent: "Silent",
};

/** WF-FIX (2026-10-26): the header's words for the rule (UX pass 0.6 (b)). */
export const WAKE_CHIP: Record<Wake, string> = {
  mentions: "wake on @",
  topic: "every topic message",
  silent: "silent",
};

export const WAKE_HELP: Record<Wake, string> = {
  mentions:
    "Agents answer when @mentioned, assigned, or replied to in a thread they started. The default.",
  topic:
    "Agents read every message in a topic they are in and answer when they have something to add.",
  silent: "Agents only post on their schedule (digests, briefs); mentions are queued for then.",
};

/** The caller's settings for one channel (defaults until they save some). */
export function usePrefs(chat: ChatContext, channel: Pick<Channel, "id">) {
  const prefs = useQuery({ ...prefsQuery(chat.api, chat.co), retry: false });
  const row = prefs.data?.data.find((p) => p.channel_id === channel.id);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (body: { joined?: boolean; muted?: boolean; notify?: Notify }) =>
      chat.api.client.channels.setPrefs({ params: { co: chat.co, id: channel.id }, body }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.prefs(chat.co) }),
        queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }),
      ]),
  });
  return {
    loaded: prefs.isSuccess,
    joined: prefs.isSuccess ? (row?.joined ?? false) : true,
    muted: row?.muted ?? false,
    notify: row?.notify ?? "mentions",
    save,
  };
}

/** The bell in the channel header: the person's notification level and mute (members only). */
export function NotificationBell({ chat, channel }: { chat: ChatContext; channel: Channel }) {
  const p = usePrefs(chat, channel);
  if (chat.meId === null || chat.readOnly) return null;
  const quiet = p.muted || p.notify === "none";
  const label = p.muted ? "Muted" : NOTIFY_LABEL[p.notify];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Notifications: ${label}`}
            className={quiet ? "text-faint" : undefined}
          />
        }
      >
        {quiet ? <BellOffIcon /> : <BellIcon />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Notify me about</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={p.notify}
            onValueChange={(v) => p.save.mutate({ notify: v as Notify })}
          >
            {(Object.keys(NOTIFY_LABEL) as Notify[]).map((k) => (
              <DropdownMenuRadioItem key={k} value={k}>
                {NOTIFY_LABEL[k]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={p.muted}
          onCheckedChange={(on) => p.save.mutate({ muted: Boolean(on) })}
        >
          Mute channel
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The "…" in the channel header: Channel details (CHAT-CONTROLS: About, Members, Settings,
 * Danger zone), Members (the same dialog on its Members tab), the digital employees' settings
 * (Settings), Saved, and Leave (notifications are the bell beside it); readers without a
 * membership (agency staff) and the grace period see the rules only.
 */
export function ChannelMenu({ chat, channel }: { chat: ChatContext; channel: Channel }) {
  const p = usePrefs(chat, channel);
  const [details, setDetails] = useState<DetailsTab | null>(null);
  const dm = isDm(channel);
  const canWrite = chat.meId !== null && !chat.readOnly;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon-sm" aria-label="Channel menu" />}
        >
          <EllipsisIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuItem onClick={() => setDetails("about")}>
            <InfoIcon />
            Channel details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDetails("members")}>
            <UsersIcon />
            Members
          </DropdownMenuItem>
          {!dm ? (
            <DropdownMenuItem onClick={() => setDetails("settings")}>
              <Settings2Icon />
              Settings and permissions
            </DropdownMenuItem>
          ) : null}
          {!dm ? (
            // CHAT-DE: the agents' rules are one row of Settings now.
            <DropdownMenuItem onClick={() => setDetails("settings")}>
              <BotIcon />
              Digital employees
            </DropdownMenuItem>
          ) : null}
          {canWrite && !dm && !channel.is_default ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() =>
                  p.save.mutate(
                    { joined: false },
                    {
                      onSuccess: () =>
                        toast.add({ title: `Left #${channel.name}`, type: "success" }),
                    },
                  )
                }
              >
                <LogOutIcon />
                Leave channel
              </DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            render={
              <Link
                to="/c/$slug/chat/saved"
                params={{ slug: chat.company.slug }}
                search={keepFocus as never}
              />
            }
          >
            <BookmarkIcon />
            Saved messages
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {details ? (
        <ChannelDetailsDialog
          chat={chat}
          channel={channel}
          tab={details}
          onClose={() => setDetails(null)}
        />
      ) : null}
    </>
  );
}

/**
 * CHAT-DE (2026-10-27): the agents' wake rule and standing instructions as one compact row of
 * Channel details › Settings — the rule a select (saved on change), the instructions behind a
 * small button. Every viewer reads them; the channel's managers change them (the API's
 * `setAgentRules` takes `access.manage`).
 */
export function AgentRulesRow({
  chat,
  channel,
  editable,
}: {
  chat: ChatContext;
  channel: Channel;
  editable: boolean;
}) {
  const rules = useQuery({ ...rulesQuery(chat.api, chat.co, channel.id), retry: false });
  return (
    <div data-slot="agent-rules" className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-foreground">Digital employees</span>
        <InfoTip label="About the digital employees' rule">
          <p className="m-0">{WAKE_HELP[rules.data?.wake ?? "mentions"]}</p>
          <p className="m-0 mt-1.5">
            Self-hosted DEs may start one thread a day here about their own work, never in quiet
            hours. "Routines only" or "Start nothing" narrows that.
          </p>
          <p className="m-0 mt-1.5 text-muted-foreground">
            Several messages while an agent works join its next turn. Channel admins change this.
          </p>
        </InfoTip>
      </div>
      {rules.isPending ? (
        <p className="m-0 text-sm text-muted-foreground">Loading the agents' rules…</p>
      ) : rules.isError ? (
        // WF-FIX (2026-10-26): a failed read is not "the default rule": say so, offer Retry.
        <p role="alert" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
          Could not load the agents' rules.
          <Button type="button" variant="secondary" size="xs" onClick={() => void rules.refetch()}>
            Retry
          </Button>
        </p>
      ) : (
        <AgentRulesFields
          key={rules.data?.updated_at ?? "default"}
          chat={chat}
          channel={channel}
          rules={rules.data}
          editable={editable}
        />
      )}
    </div>
  );
}

function AgentRulesFields({
  chat,
  channel,
  rules,
  editable,
}: {
  chat: ChatContext;
  channel: Channel;
  rules: AgentRules | undefined;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const [instructions, setInstructions] = useState(rules?.instructions ?? "");
  const [open, setOpen] = useState(false);
  const wake = rules?.wake ?? "mentions";
  const initiate = rules?.initiate ?? "allowed";
  const save = useMutation({
    mutationFn: (body: { wake: Wake; initiate?: Initiate; instructions: string | null }) =>
      chat.api.client.channels.setAgentRules({ params: { co: chat.co, id: channel.id }, body }),
    onSuccess: (r) => {
      queryClient.setQueryData(chatKeys.rules(chat.co, channel.id), r);
      toast.add({ title: "Agent settings saved", type: "success" });
      setOpen(false);
    },
    onError: (e) =>
      toast.add({
        title: "Could not save the agent settings",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  const saved = rules?.instructions ?? "";
  const dirty = instructions !== saved;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (editable && dirty) save.mutate({ wake, instructions: instructions.trim() || null });
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <NativeSelect
        aria-label="Digital employees wake on"
        className="min-w-48"
        value={wake}
        disabled={!editable || save.isPending}
        onChange={(e) =>
          editable && save.mutate({ wake: e.target.value as Wake, instructions: saved || null })
        }
      >
        {(Object.keys(WAKE_LABEL) as Wake[]).map((k) => (
          <NativeSelectOption key={k} value={k}>
            {WAKE_LABEL[k]}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <NativeSelect
        aria-label="Digital employees may start"
        className="min-w-40"
        value={initiate}
        disabled={!editable || save.isPending}
        onChange={(e) =>
          editable &&
          save.mutate({
            wake,
            initiate: e.target.value as Initiate,
            instructions: saved || null,
          })
        }
      >
        {(Object.keys(INITIATE_LABEL) as Initiate[]).map((k) => (
          <NativeSelectOption key={k} value={k}>
            {INITIATE_LABEL[k]}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-secondary"
              aria-label="Standing instructions"
            />
          }
        >
          <NotebookPenIcon data-icon="inline-start" />
          Instructions
          {saved ? <Badge variant="neutral">Set</Badge> : null}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3" data-slot="agent-instructions">
          <form onSubmit={submit} className="flex flex-col gap-2">
            <label htmlFor="agent-instructions" className="text-sm font-medium text-foreground">
              Standing instructions
            </label>
            <p className="m-0 text-xs text-muted-foreground">
              What every agent here is told before it answers.
            </p>
            <Textarea
              id="agent-instructions"
              rows={4}
              readOnly={!editable}
              value={instructions}
              placeholder={editable ? "e.g. Quote task numbers." : "None"}
              onChange={(e) => setInstructions(e.target.value)}
            />
            <div className="flex items-center justify-between gap-2">
              {rules?.updated_at ? (
                <span className="text-xs text-muted-foreground">
                  Last changed {new Date(rules.updated_at).toLocaleDateString()}
                </span>
              ) : (
                <span />
              )}
              {editable ? (
                <Button type="submit" size="sm" disabled={!dirty || save.isPending}>
                  Save
                </Button>
              ) : null}
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
}
